import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { FileHttpService } from '@sc/data-access-http-services';
import {
  Customer,
  Document,
  Dossier,
  withNativeBrowserPreview,
  withPreviewFileTypes,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { PicturesViewerService } from '@sc/feature-pictures-viewer';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  ButtonComponent,
  compressFile,
  IconComponent,
  isValidFileType,
  loadFile,
} from '@sc/ui-components';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { combineLatest, of, timer, Subscription } from 'rxjs';
import { catchError, switchMap, takeLast } from 'rxjs/operators';

@Component({
  selector: 'sc-planning-form-appointment-documents',
  styleUrls: ['./planning-form-appointment-documents.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgFor,
    IconComponent,
    ButtonComponent,
    MatMenuModule,
    NgIf,
    NgClass,
    TranslateModule,
  ],
  template: `
    <ng-container [formGroup]="form">
      <fieldset>
        <legend>
          {{ 'PlanningFormAppointmentDocuments.title' | translate }}
        </legend>

        <div
          *ngFor="let document of getDocumentsToDisplay(); let index = index"
          class="document"
        >
          <sc-icon name="document"></sc-icon>
          <span class="font-family-main font-default text-black">
            {{ document.metadata.name || document.name }}
          </span>
          <div class="actions">
            <sc-button
              id="seeBtn"
              class="see-btn"
              buttonType="mainWhite"
              leftIcon="eye"
              (click)="openDocument(document, index)"
              >{{ 'PlanningFormAppointmentDocuments.seeBtn' | translate }}
            </sc-button>
            <sc-button
              id="moreBtn"
              class="more-btn"
              buttonType="mainWhite"
              leftIcon="more_horiz"
              [iconOnly]="true"
              [matMenuTriggerFor]="menu"
            >
            </sc-button>
            <mat-menu #menu="matMenu" class="action-document">
              <button
                mat-menu-item
                class="menu-button"
                (click)="downloadDocument(document)"
              >
                <sc-icon class="download-icon" name="export"></sc-icon>
                <span>{{
                  'PlanningFormAppointmentDocuments.downloadBtn' | translate
                }}</span>
              </button>
              <button
                mat-menu-item
                class="menu-button"
                (click)="printDocument(index)"
              >
                <sc-icon class="print-icon" name="printer"></sc-icon>
                <span>{{
                  'PlanningFormAppointmentDocuments.printBtn' | translate
                }}</span>
              </button>
              <button
                mat-menu-item
                class="menu-button"
                (click)="removeDocument(index)"
              >
                <sc-icon class="delete-icon" name="delete"></sc-icon>
                <span>{{
                  'PlanningFormAppointmentDocuments.deleteBtn' | translate
                }}</span>
              </button>
            </mat-menu>
          </div>
        </div>

        <div
          *ngIf="getDocumentsToDisplay()?.length > 1"
          class="download-all-docs-btn"
        >
          <sc-button
            leftIcon="export"
            (click)="downloadAllDocuments()"
            [disabled]="areDocumentsModified()"
            >{{ 'PlanningFormAppointmentDocuments.downloadAllBtn' | translate }}
          </sc-button>
        </div>

        <div
          class="import-area"
          [ngClass]="{ 'is-dragover': isInputDragover && form?.enabled }"
          (dragover)="isInputDragover = true"
          (dragleave)="isInputDragover = false"
        >
          <div class="drag-area">
            <div class="icon-container">
              <sc-icon name="document"></sc-icon>
            </div>

            <div class="label-container">
              <sc-icon name="export"></sc-icon>
              <div
                class="label"
                [innerHTML]="
                  'PlanningFormAppointmentDocuments.dropNewFileLabel'
                    | translate
                "
              ></div>
            </div>
          </div>

          <input
            type="file"
            [accept]="mimeTypeList.join(',')"
            [disabled]="form?.disabled"
            (change)="onFileChange($event)"
          />
        </div>

        <div class="file-upload-buttons">
          <sc-button
            *ngIf="displaySendCustomerDocumentUploadLink()"
            [disabled]="
              cannotSendCustomerDocumentUploadLink() || form?.disabled
            "
            class="send-customer-link-btn mt-l"
            id="sendEmailDocumentButton"
            (click)="sendCustomerEmailDocumentUpload.emit()"
            leftIcon="mail"
          >
            {{
              'PlanningFormAppointmentDocuments.sendCustomerEmailUploadDocument'
                | translate
            }}
          </sc-button>
          <sc-button
            *ngIf="displaySendCustomerDocumentUploadLink()"
            [disabled]="
              !isSendSMSButtonActive ||
              form?.disabled ||
              isSendSMSButtonDisabled
            "
            class="send-customer-link-btn mt-l"
            id="sendSMSDocumentButton"
            (click)="onSendCustomerSMSDocumentUpload()"
            leftIcon="phone"
          >
            {{
              'PlanningFormAppointmentDocuments.sendCustomerSMSUploadDocument'
                | translate
            }}
          </sc-button>
        </div>
      </fieldset>
    </ng-container>
  `,
})
export class PlanningFormAppointmentDocumentsComponent
  implements OnInit, OnDestroy
{
  @Input() form: FormGroup;
  @Input() dossier: Dossier;
  @Input() set touched(value) {
    if (value) this.form?.get('claim').markAllAsTouched();
  }

  @Output() sendCustomerEmailDocumentUpload = new EventEmitter<void>();
  @Output() sendCustomerSMSDocumentUpload = new EventEmitter<void>();

  mimeTypeList = ['image/png', 'image/jpeg', 'application/pdf'];
  isInputDragover: boolean;
  currentFile: Document = { type: 'DOCUMENT' } as Document;
  isSendEmailButtonActive = false;
  isSendSMSButtonActive = false;
  isSendSMSButtonDisabled = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly picturesViewerService: PicturesViewerService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly fileHttpService: FileHttpService
  ) {}

  ngOnInit() {
    this.isSendEmailButtonActive = !!this.getCustomerForm()?.value?.email;
    this.isSendSMSButtonActive = !!this.getCustomerForm()?.value?.phone;
    this.changeDetectorRef.markForCheck();

    this.subscriptions.add(
      this.getDocumentsForm()?.valueChanges.subscribe(() => {
        this.changeDetectorRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.getCustomerForm()?.valueChanges.subscribe((customer: Customer) => {
        this.isSendEmailButtonActive = !!customer?.email;
        this.isSendSMSButtonActive = !!customer?.phoneNumber;
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onFileChange(event) {
    const newFile = event.target.files[0] as File & { lastModifiedDate?: Date };
    this.isInputDragover = false;

    if (newFile && isValidFileType(newFile, this.mimeTypeList)) {
      if (newFile.type === 'application/pdf') {
        this.currentFile.file = newFile;
        this.currentFile.metadata = {
          lastModifiedDate: this.currentFile.file.lastModifiedDate,
          name: this.currentFile.file.name,
          mimeType: this.currentFile.file.type,
        };
        this.updateDocumentForm();
      } else {
        compressFile(newFile)
          .pipe(
            switchMap((compressedFile) => {
              this.currentFile.file = compressedFile;
              return loadFile(this.currentFile.file);
            })
          )
          .subscribe({
            next: (fileUrl) => {
              this.currentFile.url = fileUrl;
              this.currentFile.metadata = {
                lastModifiedDate: this.currentFile.file.lastModifiedDate,
                name: this.currentFile.file.name,
                mimeType: this.currentFile.file.type,
              };
              this.updateDocumentForm();
            },
            error: () => {
              this.uploadDocumentError();
            },
          });
      }
    } else {
      this.uploadDocumentError();
    }
  }

  uploadDocumentError() {
    this.currentFile.metadata = null;
    this.currentFile.file = null;
    this.currentFile.url = null;
    this.currentFile.name = null;

    this.snackbarService.showError(
      this.translator.getInstantTranslation('Snackbar.errorTitle'),
      this.translator.getInstantTranslation(
        'PlanningFormAppointmentDocuments.errorInfo'
      )
    );
  }

  getDocumentsToDisplay() {
    return (
      this.getDocumentsForm()?.value?.filter(
        (document: Document) => document?.url || document?.file
      ) ?? []
    );
  }

  updateDocumentForm() {
    const existingDocuments = this.getDocumentsForm()?.value ?? [];

    this.getClaimForm()?.patchValue({
      documents: [...existingDocuments, { ...this.currentFile }],
    });

    this.changeDetectorRef.markForCheck();
  }

  openDocument(document: Document, index: number) {
    if (document.metadata.mimeType === 'application/pdf') {
      document = this.getDocumentsToDisplay()[index];
      let documentUrl;
      if (document.url) {
        documentUrl = document.url;
      } else {
        documentUrl = window.URL.createObjectURL(
          new Blob([document.file], { type: document.metadata.mimeType })
        );
      }
      window.open(documentUrl, '_blank');
    } else {
      this.picturesViewerService.show({
        pictures: this.getDocumentsToDisplay(),
        index: index,
        allowUnsupported: true,
      });
    }
  }

  downloadDocument(document: Document) {
    if (!document.url) {
      saveAs(
        new Blob([document.file], { type: document.metadata.mimeType }),
        document.metadata?.name || document.name
      );
    }

    saveAs(document.url, document.metadata?.name || document.name);
  }

  printDocument(index: number) {
    const docToPrint = this.getDocumentsForm()?.value?.[index] as Document;

    let newWindow: Window;
    if (withPreviewFileTypes.includes(docToPrint?.metadata?.mimeType)) {
      const newWindowHTML = `
        <!DOCTYPE html>
          <head></head>
          <body>
            <img src="${docToPrint?.url}" />
          </body>
        </html>
      `;
      newWindow = window.open();
      newWindow.document.open();
      newWindow.document.write(newWindowHTML);
      newWindow.document.close();
    } else if (
      withNativeBrowserPreview.includes(docToPrint?.metadata?.mimeType)
    ) {
      if (docToPrint?.url) {
        newWindow = window.open(docToPrint?.url);
      } else {
        newWindow = window.open(
          window.URL.createObjectURL(
            new Blob([docToPrint.file], { type: docToPrint.metadata.mimeType })
          )
        );
        return;
      }
    }

    timer(100).subscribe(() => newWindow?.print());
  }

  removeDocument(index: number) {
    let remainingDocuments: Document[] = this.getDocumentsForm()?.value;

    remainingDocuments = remainingDocuments.filter((doc, i) => i !== index);

    this.getClaimForm().patchValue({
      documents: [...remainingDocuments],
    });
  }

  areDocumentsModified() {
    const formUrlList = this.getDocumentsForm()?.value?.map(
      (document) => document.url?.split('?')[0] ?? ''
    );
    const dossierUrlList = this.dossier?.claim?.documents?.map(
      (document) => document.url?.split('?')[0] ?? ''
    );
    if (formUrlList.length !== dossierUrlList.length) return true;
    for (let i = 0; i < formUrlList.length; i++) {
      if (formUrlList[i] !== dossierUrlList[i]) return true;
    }
    return false;
  }

  downloadAllDocuments() {
    const allDocsInfos = (this.getDocumentsForm()?.value as Document[]) || [];
    combineLatest(
      allDocsInfos.map((doc) => {
        if (doc?.url) {
          return this.fileHttpService.downloadFile(doc.url)?.pipe(
            catchError((error) => {
              return of(null);
            })
          );
        } else return of(null);
      })
    )
      .pipe(takeLast(1))
      .subscribe((downloadedDocs) => {
        const zipManager = new JSZip();
        if (downloadedDocs?.length) {
          downloadedDocs.forEach((downloadedDoc, i) => {
            if (downloadedDoc) {
              zipManager.file(
                allDocsInfos[i]?.metadata?.name || allDocsInfos[i]?.name,
                downloadedDoc
              );
            }
          });
          zipManager
            .generateAsync({ type: 'blob' })
            .then((zipFile) =>
              saveAs(
                zipFile,
                this.translator.getInstantTranslation(
                  'PlanningFormAppointmentDocuments.zipFileName',
                  {
                    name: this.getCustomerForm().get('lastname').value || '',
                    brand:
                      this.getClaimForm().get('car')?.value?.manufacturer || '',
                    model: this.getClaimForm().get('car')?.value?.model || '',
                  }
                )
              )
            )
            .catch((err) => console.error(err));
        }
      });
  }

  cannotSendCustomerDocumentUploadLink() {
    return !this.isSendEmailButtonActive;
  }

  onSendCustomerSMSDocumentUpload() {
    this.sendCustomerSMSDocumentUpload.emit();

    this.isSendSMSButtonDisabled = true;
    this.changeDetectorRef.markForCheck();

    const disableTimer = timer(5 * 60 * 1000).subscribe(() => {
      this.isSendSMSButtonDisabled = false;
      this.changeDetectorRef.markForCheck();
    });

    this.subscriptions.add(disableTimer);
  }

  displaySendCustomerDocumentUploadLink() {
    return (
      this.featureFlipService.isFeatureActivated(
        'b2c-allow-document-management'
      ) && !!this.form?.get('id')?.value
    );
  }

  getCustomerForm(): FormGroup {
    return this.form?.get('customer') as FormGroup;
  }

  getClaimForm(): FormGroup {
    return this.form?.get('claim') as FormGroup;
  }

  getDocumentsForm(): FormGroup {
    return this.form?.get('claim')?.get('documents') as FormGroup;
  }
}
